/**
 * useCaptureSuggestion — "the report put this photo under: <component>" (callable fieldService_captureSuggestion).
 *
 * Read-only and derived on read: nothing is stored, so a wrong suggestion is never sticky. Asked only while the tie panel is
 * open, and only for a photo that has no label yet and came from a plate field of a report.
 */
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { captureSuggestionEligible } from '../../lib/fieldService/plateComponents';
import type { GeneratorPhoto } from '../../types/fieldService/generatorPhotos';

export interface CaptureSuggestion {
  component: string;
  via: 'component-grid-row' | 'unit-plate-field';
  fieldKey: string;
  reportAt: string | null;
}

export interface CaptureSuggestionResponse {
  photoId: string;
  suggestion: CaptureSuggestion | null;
  reason: string | null;
}

const suggestionFn = httpsCallable<{ organizationId: string; photoId: string }, CaptureSuggestionResponse>(
  functions,
  'fieldService_captureSuggestion'
);

export function useCaptureSuggestion(photo: GeneratorPhoto, open: boolean): CaptureSuggestion | null {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const { data } = useQuery({
    queryKey: ['capture-suggestion', orgId, photo.id],
    queryFn: async () => (await suggestionFn({ organizationId: orgId, photoId: photo.id })).data,
    enabled: open && orgId !== '' && captureSuggestionEligible(photo),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  return captureSuggestionEligible(photo) ? (data?.suggestion ?? null) : null;
}
